/**
 * Etapa 4 do autocadastro — a revisão antes do envio.
 *
 * Mostra o que vai ser protocolado: a entidade, os contatos, cada veículo com
 * modalidade, DRE, escolas em ordem e cooperativa, e os aceites. "Enviar
 * inscrição" é do assistente (rodapé); esta etapa só confere.
 *
 * Quando o servidor devolve PENDÊNCIAS (ele relê o SGTP e o EOL no envio, e o
 * que a tela aceitou pode ter mudado), elas aparecem aqui num alerta FIXO, com
 * a placa de cada uma — aviso que some deixaria o inscrito sem saber o que
 * corrigir. Nada foi enviado nesse caso, e o alerta diz isso.
 */

import { AlertTriangle, Car, Check, Mail, MapPin, School, Users } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';

import {
  useCooperativasDaInscricao,
  useEscolasDaInscricao,
  type CicloDaInscricao,
  type ContatosDoRascunho,
  type DadosDoRascunho,
  type Pendencia,
  type Rascunho,
  type VeiculoDoRascunho,
} from './apiDaInscricao';
import {
  ROTULO_DA_MODALIDADE,
  comPlacaFormatada,
  formatarCnpj,
  formatarPlaca,
  formatarTelefone,
} from './contrato';

const ROTULO_DA_ENTIDADE = {
  PF: 'Pessoa física',
  PJ: 'Pessoa jurídica',
  COOPERATIVA: 'Cooperativa',
};

export interface EtapaConfirmacaoProps {
  ciclo: CicloDaInscricao;
  entidade: Rascunho['entidade'];
  contatos: ContatosDoRascunho;
  dados: DadosDoRascunho;
  pendencias: Pendencia[];
}

export function EtapaConfirmacao({
  ciclo,
  entidade,
  contatos,
  dados,
  pendencias,
}: EtapaConfirmacaoProps) {
  const cooperativas = useCooperativasDaInscricao();
  const nomeDaCooperativa = (cnpj: string | null) =>
    cnpj ? (cooperativas.data?.find((c) => c.cnpj === cnpj)?.nome ?? cnpj) : null;

  return (
    <div className="space-y-4">
      {pendencias.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle aria-hidden="true" className="h-4 w-4" />
          <AlertTitle>Nada foi enviado: faltam ajustes</AlertTitle>
          <AlertDescription>
            <ul className="mt-1 list-disc space-y-1 pl-4">
              {pendencias.map((p, i) => (
                <li key={`${p.campo}-${p.placa ?? ''}-${i}`}>
                  {comPlacaFormatada(p.mensagem, p.placa)}
                </li>
              ))}
            </ul>
            <p className="mt-2">Volte à etapa indicada, corrija e envie de novo.</p>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{entidade.nome}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm text-muted-foreground">
          <p>
            {ROTULO_DA_ENTIDADE[entidade.tipo]} ·{' '}
            <span className="font-mono">{formatarCnpj(entidade.documento)}</span>
          </p>
          <p className="flex flex-wrap items-center gap-1.5">
            <Mail aria-hidden="true" className="h-3.5 w-3.5" />
            {contatos.email}
            {contatos.telefone ? ` · ${formatarTelefone(contatos.telefone)}` : ''}
          </p>
          <p>Período: {ciclo.nome}</p>
        </CardContent>
      </Card>

      {dados.veiculos.map((veiculo) => (
        <ResumoDoVeiculo
          key={veiculo.placa}
          ciclo={ciclo}
          veiculo={veiculo}
          cooperativa={nomeDaCooperativa(veiculo.cooperativaCnpj)}
        />
      ))}

      <Card>
        <CardContent className="space-y-1 p-4 text-sm">
          <p className="flex items-center gap-1.5">
            <Check aria-hidden="true" className="h-4 w-4 text-success" />
            Edital aceito — versão {dados.editalVersao ?? ciclo.editalVersao}
          </p>
          <p className="flex items-center gap-1.5">
            <Check aria-hidden="true" className="h-4 w-4 text-success" />
            Aviso de privacidade aceito
          </p>
        </CardContent>
      </Card>

      <p className="text-sm text-muted-foreground">
        Ao enviar, cada veículo recebe o seu protocolo, e o comprovante com todos eles chega ao
        e-mail informado.
      </p>
    </div>
  );
}

function ResumoDoVeiculo({
  ciclo,
  veiculo,
  cooperativa,
}: {
  ciclo: CicloDaInscricao;
  veiculo: VeiculoDoRascunho;
  cooperativa: string | null;
}) {
  const escolas = useEscolasDaInscricao(veiculo.dreId, veiculo.modalidade);
  const nome = (codigo: string) =>
    escolas.data?.find((e) => e.codigoEol === codigo)?.nome ?? `EOL ${codigo}`;
  const dre = ciclo.dres.find((d) => d.id === veiculo.dreId);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <Car aria-hidden="true" className="h-4 w-4 text-primary" />
          <span className="font-mono">{formatarPlaca(veiculo.placa)}</span>
          {veiculo.modalidade && (
            <Badge variant="secondary">{ROTULO_DA_MODALIDADE[veiculo.modalidade]}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="flex items-center gap-1.5 text-muted-foreground">
          <MapPin aria-hidden="true" className="h-3.5 w-3.5" />
          {dre?.nome ?? 'DRE não escolhida'}
        </p>
        <div className="flex items-start gap-1.5">
          <School aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 text-muted-foreground" />
          <ol className="list-decimal space-y-0.5 pl-4">
            {veiculo.escolas.map((codigo) => (
              <li key={codigo}>{nome(codigo)}</li>
            ))}
          </ol>
        </div>
        {cooperativa && (
          <p className="flex items-center gap-1.5 text-muted-foreground">
            <Users aria-hidden="true" className="h-3.5 w-3.5" />
            Cooperativa indicada: {cooperativa}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default EtapaConfirmacao;
